import Button from 'components/Button'
import {
  TNpsQuestion,
  TNpsQuestionValue,
  TNpsSubmitAnswer,
  useGetNpsForm,
  useSendNpsFirstAnswer,
  useSubmitNpsForm
} from '@cannect/services/resources/nps'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import QuestionField from './QuestionField'
import * as Style from './styles'

type NpsPathParams = {
  token: string
}

const SECTION_NONE = '__no_section__'

const isEmptyValue = (value: TNpsQuestionValue): boolean => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

const submitErrorMessage = (status?: number): string => {
  if (status === 409) return 'Este formulário já foi enviado anteriormente.'
  if (status === 410) return 'Este link expirou.'
  if (status === 400) return 'Verifique os valores informados e tente novamente.'
  return 'Erro ao enviar. Tente novamente.'
}

const Spinner = () => (
  <Style.SpinnerWrapper>
    <div
      style={{
        width: 44,
        height: 44,
        border: '4px solid #e5e7eb',
        borderTopColor: '#1e40af',
        borderRadius: '50%',
        animation: 'nps-spin 0.8s linear infinite'
      }}
    />
    <style>{`@keyframes nps-spin { to { transform: rotate(360deg); } }`}</style>
  </Style.SpinnerWrapper>
)

export default function NPS() {
  const { token } = useParams<NpsPathParams>()
  const { data, isLoading, isError, error } = useGetNpsForm(token)
  const firstAnswerMutation = useSendNpsFirstAnswer(token)
  const submitMutation = useSubmitNpsForm(token)

  const [answers, setAnswers] = useState<Record<string, TNpsQuestionValue>>({})
  const [extraTexts, setExtraTexts] = useState<Record<string, string>>({})
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)
  const firstAnswerSentRef = useRef(false)

  const scoreFromUrl = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('score')
    if (raw === null) return null
    const num = Number(raw)
    return Number.isFinite(num) ? num : null
  }, [])

  // Inicializa respostas com current_value vindo do backend
  useEffect(() => {
    if (!data?.form?.questions) return
    setAnswers((prev) => {
      const next = { ...prev }
      data.form.questions.forEach((q) => {
        if (next[q.uuid] === undefined) {
          next[q.uuid] = q.current_value ?? (q.type === 'multi_choice' ? [] : null)
        }
      })
      return next
    })
  }, [data])

  // First-answer silencioso: chama uma vez ao carregar com score da URL ou current_value da primeira pergunta
  useEffect(() => {
    if (firstAnswerSentRef.current) return
    if (!data?.form?.questions?.length) return
    if (data.submitted_at) return

    const firstQuestion = data.form.questions.find((q) => q.is_first_question)
    if (!firstQuestion) return

    let score: number | null = null
    if (scoreFromUrl !== null) {
      score = scoreFromUrl
    } else if (typeof firstQuestion.current_value === 'number') {
      // já gravado anteriormente — não precisa reenviar
      return
    } else {
      return
    }

    firstAnswerSentRef.current = true
    firstAnswerMutation.mutate(
      { score, question_uuid: firstQuestion.uuid },
      {
        onError: (err) => {
          // background — apenas loga
          console.error('NPS first-answer failed', err)
        }
      }
    )

    // Reflete localmente a estrela selecionada
    setAnswers((prev) => ({ ...prev, [firstQuestion.uuid]: score }))
  }, [data, scoreFromUrl, firstAnswerMutation])

  const handleChange = (questionUuid: string, value: TNpsQuestionValue) => {
    setAnswers((prev) => ({ ...prev, [questionUuid]: value }))
    setValidationErrors((prev) => {
      if (!prev[questionUuid]) return prev
      const next = { ...prev }
      delete next[questionUuid]
      return next
    })
  }

  const handleExtraTextChange = (questionUuid: string, text: string) => {
    setExtraTexts((prev) => ({ ...prev, [questionUuid]: text }))
  }

  const groupedSections = useMemo(() => {
    if (!data?.form?.questions) return []
    const sorted = [...data.form.questions].sort((a, b) => a.order - b.order)
    const map = new Map<string, TNpsQuestion[]>()
    sorted.forEach((q) => {
      const key = q.section_title ?? SECTION_NONE
      const arr = map.get(key) ?? []
      arr.push(q)
      map.set(key, arr)
    })
    return Array.from(map.entries()).map(([title, questions]) => ({
      title: title === SECTION_NONE ? null : title,
      questions
    }))
  }, [data])

  const handleSubmit = () => {
    if (!data?.form?.questions) return

    const errors: Record<string, string> = {}
    data.form.questions.forEach((q) => {
      if (q.required && isEmptyValue(answers[q.uuid])) {
        errors[q.uuid] = 'Esta resposta é obrigatória'
      }
    })

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      const firstErrorId = Object.keys(errors)[0]
      document.getElementById(firstErrorId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    const payload: TNpsSubmitAnswer[] = data.form.questions
      .filter((q) => !isEmptyValue(answers[q.uuid]))
      .map((q) => ({
        question_uuid: q.uuid,
        value: answers[q.uuid],
        ...(extraTexts[q.uuid] ? { extra_text: extraTexts[q.uuid] } : {})
      }))

    submitMutation.mutate(
      { answers: payload },
      {
        onSuccess: () => setSubmitted(true)
      }
    )
  }

  if (isLoading) {
    return (
      <Style.PageContainer>
        <Spinner />
      </Style.PageContainer>
    )
  }

  if (isError) {
    const status = (error as AxiosError)?.response?.status
    let message = 'Não foi possível carregar o formulário. Tente novamente em instantes.'
    if (status === 410) message = 'Este link expirou. Entre em contato para receber um novo.'
    else if (status === 404) message = 'Link inválido. Verifique o email recebido.'

    return (
      <Style.PageContainer>
        <Style.StateBox>
          <h2>Ops!</h2>
          <p>{message}</p>
        </Style.StateBox>
      </Style.PageContainer>
    )
  }

  if (!data) return null

  const alreadySubmitted = submitted || Boolean(data.submitted_at)

  if (alreadySubmitted) {
    return (
      <Style.PageContainer>
        <Style.StateBox>
          <h2>Obrigado!</h2>
          <p>{data.form.thank_you_message || 'Sua resposta foi registrada. Agradecemos seu feedback!'}</p>
        </Style.StateBox>
      </Style.PageContainer>
    )
  }

  const submitError = submitMutation.error as AxiosError | null
  const submitStatus = submitError?.response?.status

  return (
    <Style.PageContainer>
      <Style.ContentWrapper>
        <Style.Header>
          <h1>{data.form.name}</h1>
          <div dangerouslySetInnerHTML={{ __html: data.form.description || '' }} />
        </Style.Header>

        {groupedSections.map((section, idx) => (
          <Style.Card key={section.title ?? `section-${idx}`}>
            {section.title && <Style.SectionTitle>{section.title}</Style.SectionTitle>}
            {section.questions.map((q) => (
              <div key={q.uuid} id={q.uuid}>
                <QuestionField
                  question={q}
                  value={answers[q.uuid] ?? (q.type === 'multi_choice' ? [] : null)}
                  onChange={(value) => handleChange(q.uuid, value)}
                  extraText={extraTexts[q.uuid] ?? ''}
                  onExtraTextChange={(text) => handleExtraTextChange(q.uuid, text)}
                />
                {validationErrors[q.uuid] && <Style.ErrorMessage>{validationErrors[q.uuid]}</Style.ErrorMessage>}
              </div>
            ))}
          </Style.Card>
        ))}

        {submitError && (
          <Style.ErrorMessage style={{ textAlign: 'center' }}>{submitErrorMessage(submitStatus)}</Style.ErrorMessage>
        )}

        <Style.SubmitWrapper>
          <Button onClick={handleSubmit} disabled={submitMutation.isPending}>
            {submitMutation.isPending ? 'Enviando...' : 'Enviar'}
          </Button>
        </Style.SubmitWrapper>
      </Style.ContentWrapper>
    </Style.PageContainer>
  )
}
